/**
 * 多租户微信公众号订阅通知 · H5 最小增量
 *
 * 设计原则：
 * - 非微信环境一律直接返回，不影响任何既有流程
 * - 静默绑定：仅当入口带 wxappid/ctk（公众号菜单/推广链接）时，每会话做一次
 *   snsapi_base 静默授权；回调 wxoid 已登录立即 bind，游客暂存 sessionStorage，
 *   登录/注册后路由守卫自动补交
 * - 订阅授权：默认模式2（一次性订阅，微信官方确认页，支持点击跳回业务 H5）；
 *   多个类型走确认队列（确认一个回来再继续下一个），用户取消即终止
 * - 模式1（bizsend）需 <wx-open-subscribe> 开放标签承接用户点击手势，
 *   不在关键动作中做无手势强弹（避免打断主流程），触点静默跳过
 * - 所有异常/未配置/取消全程静默，不产生 console 噪音、不阻断业务
 */
import { bindWxOpenid, prepareSubscribe } from '@/api/wxNotify'
import { useUserStore } from '@/store'
import { isWechat } from '@/utils/wechatShare'

/** 后端接口基址（与 axios 一致，如 /api；顶级跳转用） */
const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

/** 游客待绑定 openid 交换码（登录/注册后自动补交） */
const PENDING_WXOID_KEY = 'wxnotify:wxoid'
/** 静默授权去重标记（按入口 appid/ctk，每会话至多一次，防跳转环） */
const OAUTH_DONE_PREFIX = 'wxnotify:oauth:done:'
/** 一次性订阅确认队列（模式2 多类型串行确认） */
const SUBSCRIBE_QUEUE_KEY = 'wxnotify:sub-queue'

/** 微信回调附加的 hash query 参数（落地后清理，保持 URL 干净） */
const CALLBACK_PARAMS = ['wxoid', 'wxfail', 'wxsub', 'wxtype', 'wxbound']

/** 取 hash 内 path（/xxx）与 query 对象 */
function parseHash(): { path: string; params: URLSearchParams } {
  const hash = window.location.hash || ''
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  const qIndex = raw.indexOf('?')
  if (qIndex < 0) {
    return { path: raw || '/', params: new URLSearchParams() }
  }
  return {
    path: raw.slice(0, qIndex) || '/',
    params: new URLSearchParams(raw.slice(qIndex + 1))
  }
}

/** 重写 hash（清掉微信回调参数与一次性入口参数），不刷新页面 */
function replaceHashQuery(params: URLSearchParams, removeKeys: string[]) {
  removeKeys.forEach((k) => params.delete(k))
  const { path } = parseHash()
  const query = params.toString()
  const newHash = '#' + path + (query ? `?${query}` : '')
  const url = window.location.pathname + window.location.search + newHash
  window.history.replaceState(null, '', url)
}

/** 保存游客待绑定码 */
function savePendingWxoid(wxoid: string) {
  try {
    sessionStorage.setItem(PENDING_WXOID_KEY, wxoid)
  } catch {
    /* 隐私模式等场景忽略，绑定仅影响通知能力 */
  }
}

function takePendingWxoid(): string {
  try {
    return sessionStorage.getItem(PENDING_WXOID_KEY) || ''
  } catch {
    return ''
  }
}

function clearPendingWxoid() {
  try {
    sessionStorage.removeItem(PENDING_WXOID_KEY)
  } catch {
    /* ignore */
  }
}

function readQueue(): string[] {
  try {
    const raw = sessionStorage.getItem(SUBSCRIBE_QUEUE_KEY)
    const arr = raw ? JSON.parse(raw) : []
    return Array.isArray(arr) ? arr.filter((t) => typeof t === 'string') : []
  } catch {
    return []
  }
}

function writeQueue(types: string[]) {
  try {
    if (types.length) {
      sessionStorage.setItem(SUBSCRIBE_QUEUE_KEY, JSON.stringify(types))
    } else {
      sessionStorage.removeItem(SUBSCRIBE_QUEUE_KEY)
    }
  } catch {
    /* ignore */
  }
}

/**
 * 初始化（路由 afterEach 调用，幂等）：
 * 1. 清理/核销微信回调参数，wxoid 登录即绑定
 * 2. 登录态下补交游客期间暂存的 wxoid
 * 3. 串行确认队列（模式2 从微信确认页回来后继续下一个类型）
 * 4. 公众号入口（wxappid/ctk）每会话一次静默授权
 */
export function initWxNotify(): void {
  if (!isWechat()) return
  try {
    const { params } = parseHash()
    const hasCallback = CALLBACK_PARAMS.some((k) => params.has(k))

    const wxoid = params.get('wxoid') || ''
    if (wxoid) savePendingWxoid(wxoid)

    const userStore = useUserStore()
    const pending = takePendingWxoid()
    if (pending && userStore.isLogin) {
      void bindPendingWxoid(pending)
    }

    if (params.has('wxsub')) {
      const result = params.get('wxsub')
      const queue = readQueue()
      if (result === 'granted') {
        // 当前类型已由后端在确认回调中累加额度，继续队列下一个
        queue.shift()
        writeQueue(queue)
        if (queue.length) {
          replaceHashQuery(params, CALLBACK_PARAMS)
          void proceedSubscribe(queue[0])
          return
        }
      } else {
        // cancel / pending(游客) / invalid_template：终止串行
        writeQueue([])
      }
    }

    if (hasCallback) {
      replaceHashQuery(params, CALLBACK_PARAMS)
    }

    // 公众号入口静默授权（每会话每入口至多一次；回调参数在场时绝不重入）
    if (!hasCallback) {
      maybeSilentOauth(params)
    }
  } catch {
    /* 初始化任何异常都静默，绝不影响页面 */
  }
}

/** 登录/注册成功后可显式调用（路由守卫已兜底，页面无需强制接入） */
export async function bindPendingWxoid(wxoid?: string): Promise<void> {
  const code = wxoid || takePendingWxoid()
  if (!code) return
  try {
    const res = await bindWxOpenid(code)
    if (res?.code === 200) {
      clearPendingWxoid()
    }
  } catch {
    /* 失败保留 wxoid，下次路由切换重试；静默 */
  }
}

/**
 * 关键动作触点：请求订阅授权（单行非阻断调用）。
 * @param types 模板类型，如 ['remind'] / ['appointment','remind']
 */
export function requestSubscribe(types: string[]): void {
  if (!isWechat() || !types || !types.length) return
  const userStore = useUserStore()
  if (!userStore.isLogin) return
  try {
    writeQueue(types)
    void proceedSubscribe(types[0])
  } catch {
    /* 静默 */
  }
}

/** 准备单个类型并按模式处理（模式2整页跳确认页） */
async function proceedSubscribe(type: string): Promise<void> {
  const redirect = parseHash().path + (window.location.hash.includes('?')
    ? '?' + (window.location.hash.split('?')[1] || '')
    : '')
  const res = await prepareSubscribe(type, redirect)
  const data = res?.data
  if (res?.code !== 200 || !data) {
    // 未配置/未绑定等：清理队列，静默跳过
    writeQueue([])
    return
  }
  if (data.mode === 2 && data.confirmUrl) {
    window.location.replace(data.confirmUrl)
    return
  }
  // 模式1：需页面放置 wx-open-subscribe 开放标签由用户点击触发，此处不强弹不打断
  writeQueue([])
}

/** 公众号菜单/推广入口静默授权（携带 wxappid 或 ctk） */
function maybeSilentOauth(params: URLSearchParams): void {
  const wxappid = (params.get('wxappid') || '').trim()
  const ctk = (params.get('ctk') || '').trim()
  if (!wxappid && !ctk) return

  const entryKey = wxappid || ctk
  try {
    if (sessionStorage.getItem(OAUTH_DONE_PREFIX + entryKey)) return
    sessionStorage.setItem(OAUTH_DONE_PREFIX + entryKey, '1')
  } catch {
    /* ignore */
  }

  const { path, params: allParams } = parseHash()
  const clean = new URLSearchParams(allParams.toString())
  clean.delete('wxappid')
  clean.delete('ctk')
  const redirect = path + (clean.toString() ? `?${clean.toString()}` : '')

  const query = new URLSearchParams()
  if (wxappid) query.set('wxappid', wxappid)
  if (ctk) query.set('ctk', ctk)
  query.set('redirect', redirect)
  // 顶级跳转走微信授权；后端 @PublicAccess，无需也无法携带 XHR 的 JWT，
  // 已登录用户同样走 wxoid 回调落地后 XHR 补交绑定
  window.location.replace(`${API_BASE}/wx-notify/oauth/entry?${query.toString()}`)
}
